/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.row = this.querySelector('.announcement__row');
      this.rightCol = this.querySelector('.announcement__col--right');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }

      if (this.row && this.rightCol) {
        this.resizeObserver = new ResizeObserver(this.setReserve.bind(this));
        this.resizeObserver.observe(this.row);
        this.resizeObserver.observe(this.rightCol);
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            // Scroll by slide width rather than offsetLeft: offsetLeft is
            // measured from whichever ancestor happens to be positioned, so it
            // silently picks up the container gutter and needs correcting by a
            // gutter width that is not the same at every breakpoint.
            const slideWidth = slides[nextSlideIndex].clientWidth;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(-Math.abs(nextSlideIndex * slideWidth), 0);
            } else {
              this.slider.scrollTo(nextSlideIndex * slideWidth, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }

      this.setReserve();
    }

    /**
     * Mirrors the width of the links/country selector column as an empty
     * reserve on the opposite side of the row, so the announcement text stays
     * centred on the bar while its slider stops short of that column rather
     * than scrolling underneath it. Reserving nothing is the fallback when the
     * column is wide enough that mirroring it would squeeze the text.
     */
    setReserve() {
      if (!this.row || !this.rightCol) return;

      // The column is hidden below the desktop breakpoint, where the row is
      // not a grid and the reserve is unused anyway.
      const rightWidth = this.rightCol.offsetWidth;
      const rowWidth = this.row.offsetWidth;
      const fits = rowWidth > 0 && rightWidth * 2 < rowWidth * 0.6;
      const reserve = `${fits ? rightWidth : 0}px`;

      // Writing the same value back would keep the resize observer looping.
      if (reserve === this.reserve) return;
      this.reserve = reserve;
      this.row.style.setProperty('--announcement-reserve', reserve);
    }

    /**
     * The help links stay in the announcement bar on all screen sizes (the
     * full screen mobile menu doesn't include them); this only removes links
     * that an earlier version injected into the menu.
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (menuAnnouncementLinks) menuAnnouncementLinks.remove();
    }

    /**
     * The localization selector stays in the announcement bar on all screen
     * sizes (the full screen mobile menu doesn't include it); this only
     * restores a selector that an earlier version moved into the menu.
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      const menuLocalization = document.querySelector('.mob__localization');
      if (menuLocalization) {
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
